// My Organisations — cross-project organisation list
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Search, Building2, Plus, Trash2 } from 'lucide-react'
import { getMyOrgs, createCrmOrg, deleteCrmOrg, getProjects } from '../../../services/api'
import { disambiguateProjectLabels } from '../../../utils/projects'
import type { CrmOrganisation, Project } from '../../../types'
import PageHeader from '../../../components/ui/PageHeader'
import EmptyState from '../../../components/ui/EmptyState'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import CrmLoadError from '../../../components/crm/CrmLoadError'
import CrmTabs from '../../../components/crm/CrmTabs'
import { projectLabel } from '../../../components/crm/crmProjects'

export default function MyOrganisations() {
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<CrmOrganisation[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<CrmOrganisation | null>(null)
  const [orgName, setOrgName] = useState('')
  const [domain, setDomain] = useState('')
  const [industry, setIndustry] = useState('')
  const [projectId, setProjectId] = useState('')

  const load = () => {
    setLoading(true)
    setLoadError(false)
    // A failed request must show an error with Retry, never an empty list (UX-038).
    Promise.all([getMyOrgs(), getProjects()])
      .then(([orgData, projectData]) => {
        setOrgs(orgData.organisations || [])
        const projectList = Array.isArray(projectData) ? projectData : []
        setProjects(projectList)
        if (projectList.length > 0 && !projectId) setProjectId(projectList[0].project_id)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const filtered = orgs.filter((o) => {
    const q = search.toLowerCase()
    return !q || `${o.org_name} ${o.domain} ${o.industry}`.toLowerCase().includes(q)
  })

  const resetForm = () => {
    setOrgName(''); setDomain(''); setIndustry(''); setError('')
    setShowForm(false)
  }

  const handleCreate = async () => {
    if (!orgName.trim()) return
    if (!projectId) { setError('Please select a project'); return }
    setSaving(true)
    setError('')
    try {
      const org = await createCrmOrg({
        org_name: orgName.trim(),
        domain: domain.trim(),
        industry: industry.trim(),
        project_id: projectId,
      })
      setOrgs((prev) => [org, ...prev])
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organisation')
    } finally {
      setSaving(false)
    }
  }

  // Honest delete (P7): the real mutation runs inside the ConfirmDialog. On
  // failure it throws so the dialog stays open and surfaces the message.
  const confirmDelete = async () => {
    if (!pendingDelete) return
    await deleteCrmOrg(pendingDelete.org_id, pendingDelete.project_id)
    setOrgs((prev) => prev.filter((x) => x.org_id !== pendingDelete.org_id))
  }

  return (
    <div className="space-y-4">
      <Link to="/crm" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft size={16} /> Back to CRM
      </Link>

      <CrmTabs />

      <PageHeader
        title="My Organisations"
        description="Every organisation you've added across your projects, in one place."
        helpKey="crm"
        status={loadError || loading ? undefined : { label: `(${filtered.length})` }}
        primaryAction={
          <button
            onClick={() => { setError(''); setShowForm(true) }}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 transition-colors"
          >
            <Plus size={16} /> Add Organisation
          </button>
        }
      />

      {showForm && (
        <div className="light-card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">New Organisation</h3>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
          <div className="grid grid-cols-3 gap-3">
            <input type="text" placeholder="Organisation name *" value={orgName} onChange={(e) => setOrgName(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            <input type="text" placeholder="Domain" value={domain} onChange={(e) => setDomain(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            <input type="text" placeholder="Industry" value={industry} onChange={(e) => setIndustry(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          {projects.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              An organisation belongs to a project. <Link to="/projects" className="font-medium underline">Create a project first</Link>, then come back to add organisations.
            </p>
          ) : (
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              aria-label="Project"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
            >
              <option value="">Select a project *</option>
              {disambiguateProjectLabels(projects).map((p) => (
                <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={resetForm} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
            <button onClick={handleCreate} disabled={!orgName.trim() || !projectId || saving}
              className="px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 disabled:opacity-50 transition-colors">
              {saving ? 'Saving...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Search by name or domain..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {/* Content */}
      {loadError ? (
        <CrmLoadError what="your organisations" onRetry={load} />
      ) : loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-sky-500/10 rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={search ? 'No organisations match your search' : 'No organisations yet'}
          description={search
            ? 'Try a different name or domain.'
            : 'Organisations you add to any project appear here, across your whole portfolio.'}
          action={search ? undefined : (
            <button
              onClick={() => { setError(''); setShowForm(true) }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600"
            >
              <Plus size={16} /> Add your first organisation
            </button>
          )}
        />
      ) : (
        <div className="light-card overflow-hidden">
          <table className="min-w-full divide-y divide-sky-200/50">
            <thead className="bg-sky-500/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sky-200/30">
              {filtered.map((o) => (
                <tr
                  key={o.org_id}
                  onClick={(e) => {
                    // Mouse convenience only: the name is a real link for keyboard users.
                    if ((e.target as HTMLElement).closest('a,button')) return
                    navigate(`/project/${o.project_id}/crm/organisations/${o.org_id}`)
                  }}
                  className="hover:bg-white/30 cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center text-xs font-semibold text-purple-700 shrink-0">
                        {o.org_name?.[0]}
                      </div>
                      <Link
                        to={`/project/${o.project_id}/crm/organisations/${o.org_id}`}
                        className="text-sm font-medium text-gray-900 hover:text-sky-700 hover:underline"
                      >
                        {o.org_name}
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{o.domain || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{o.industry || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{projectLabel(projects, o.project_id)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); setPendingDelete(o) }}
                      className="text-gray-600 hover:text-red-500 transition-colors"
                      title="Delete organisation"
                      aria-label={`Delete organisation ${o.org_name}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete organisation?"
        message={
          pendingDelete
            ? `This permanently removes ${pendingDelete.org_name} from its project. This can't be undone.`
            : ''
        }
        confirmLabel="Delete organisation"
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  )
}
